<template>
  <div class="wheel-column">
    <div
      ref="list"
      class="wheel-column__list"
      @scroll="onScroll"
    >
      <div
        v-for="(item, index) in repeatedItems"
        :key="`${item.value}-${index}`"
        class="wheel-column__item"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script>
const ITEM_HEIGHT = 36;
const REPEAT_COUNT = 80;
const EDGE_CYCLES = 5;

export default {
  name: 'WheelColumn',

  props: {
    values: {
      type: Array,
      required: true,
    },
    value: {
      type: [Number, String],
      required: true,
    },
    pad: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      scrolling: false,
      scrollTimer: null,
      suppressScroll: false,
    };
  },

  computed: {
    repeatedItems() {
      const items = this.values.map((val) => ({
        value: val,
        label: this.pad ? String(val).padStart(2, '0') : String(val),
      }));

      const repeated = [];
      for (let i = 0; i < REPEAT_COUNT; i += 1) {
        repeated.push(...items);
      }

      return repeated;
    },

    cycleLength() {
      return this.values.length;
    },
  },

  watch: {
    value() {
      if (!this.scrolling) {
        this.scrollToValue(this.value, false);
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.scrollToValue(this.value, false);
    });
  },

  methods: {
    getIndexForValue(val) {
      const index = this.values.indexOf(val);
      return index >= 0 ? index : 0;
    },

    getMiddleIndex(valueIndex) {
      const middleCycle = Math.floor(REPEAT_COUNT / 2);
      return middleCycle * this.cycleLength + valueIndex;
    },

    scrollToValue(val, smooth) {
      const list = this.$refs.list;
      if (!list) return;

      const valueIndex = this.getIndexForValue(val);
      const itemIndex = this.getMiddleIndex(valueIndex);

      this.suppressScroll = true;
      list.scrollTo({
        top: itemIndex * ITEM_HEIGHT,
        behavior: smooth ? 'smooth' : 'auto',
      });

      requestAnimationFrame(() => {
        this.suppressScroll = false;
      });
    },

    onScroll() {
      if (this.suppressScroll) return;

      this.scrolling = true;
      this.emitFromScrollPosition(false);
      clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        this.snapAndEmit();
        this.scrolling = false;
      }, 80);
    },

    emitFromScrollPosition(shouldReposition) {
      const list = this.$refs.list;
      if (!list || !this.cycleLength) return null;

      const rawIndex = Math.round(list.scrollTop / ITEM_HEIGHT);
      const valueIndex = ((rawIndex % this.cycleLength) + this.cycleLength) % this.cycleLength;
      const newValue = this.values[valueIndex];

      if (shouldReposition) {
        const middleIndex = this.getMiddleIndex(valueIndex);
        const minSafeIndex = EDGE_CYCLES * this.cycleLength;
        const maxSafeIndex = (REPEAT_COUNT - EDGE_CYCLES) * this.cycleLength;

        this.suppressScroll = true;

        if (rawIndex < minSafeIndex || rawIndex > maxSafeIndex) {
          list.scrollTop = middleIndex * ITEM_HEIGHT;
        } else {
          list.scrollTop = rawIndex * ITEM_HEIGHT;
        }

        requestAnimationFrame(() => {
          this.suppressScroll = false;
        });
      }

      if (newValue !== this.value) {
        this.$emit('change', newValue);
      }

      return newValue;
    },

    snapAndEmit() {
      this.emitFromScrollPosition(true);
    },
  },
};
</script>

<style scoped>
.wheel-column {
  position: relative;
  width: 70px;
  height: 180px;
  overflow: hidden;
}

.wheel-column__list {
  height: 100%;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 72px 0;
}

.wheel-column__list::-webkit-scrollbar {
  display: none;
}

.wheel-column__item {
  height: 36px;
  line-height: 36px;
  text-align: center;
  font-size: 20px;
  color: #374151;
  scroll-snap-align: center;
  user-select: none;
}
</style>
